import React, { useEffect, useMemo, useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { PiFunnelX } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { TCCredentialKind } from 'src/api/generated';
import SearchableSelect, {
  type SearchableSelectItem,
} from 'src/modules/shared/components/atoms/inputs/searchable-select';
import {
  CREDENTIAL_PROVIDER_OPTIONS,
  CREDENTIAL_KIND_LABEL_KEY_BY_KIND,
  CredentialKindFilter,
  OTHER_CREDENTIAL_PROVIDER_OPTION,
} from 'src/modules/settings/utils/credentials.util';

/**
 * Props for the CredentialsFilters component.
 */
export interface CredentialsFiltersProps {
  /** Provider filter input value. */
  provider: string;
  /** Called when provider changes. */
  onProviderChange: (value: string) => void;
  /** Kind filter value. */
  kind: CredentialKindFilter;
  /** Called when kind changes. */
  onKindChange: (value: CredentialKindFilter) => void;
  /** Called when filters should be reset. */
  onClear: () => void;
}

/**
 * CredentialsFilters component.
 *
 * Renders provider and kind filters for the credentials settings page.
 */
export const CredentialsFilters: React.FC<CredentialsFiltersProps> = ({
  provider,
  onProviderChange,
  kind,
  onKindChange,
  onClear,
}) => {
  const { t } = useI18n();

  const [providerOption, setProviderOption] = useState<string>('');

  useEffect(() => {
    const normalized = provider?.trim() ?? '';
    if (normalized.length === 0) {
      setProviderOption('');
      return;
    }

    if (normalized === OTHER_CREDENTIAL_PROVIDER_OPTION) {
      setProviderOption(OTHER_CREDENTIAL_PROVIDER_OPTION);
      return;
    }

    const isSupportedProvider = CREDENTIAL_PROVIDER_OPTIONS.some(
      (option) => option.id === normalized
    );
    if (isSupportedProvider) {
      setProviderOption(normalized);
      return;
    }

    setProviderOption(OTHER_CREDENTIAL_PROVIDER_OPTION);
  }, [provider]);

  const providerOptions: SearchableSelectItem[] = useMemo(() => {
    const options: SearchableSelectItem[] = [
      { value: '', content: t('settings.credentials.provider.all') },
    ];

    if (CREDENTIAL_PROVIDER_OPTIONS.length > 0) {
      options.push({ separator: true });
      options.push(
        ...CREDENTIAL_PROVIDER_OPTIONS.map((p) => ({
          value: p.id,
          content: t(p.labelKey),
        }))
      );
      options.push({ separator: true });
    }

    options.push({
      value: OTHER_CREDENTIAL_PROVIDER_OPTION,
      content: t('settings.credentials.provider.other'),
    });

    return options;
  }, [t]);

  const kindOptions = useMemo(
    () => [
      {
        id: 'all',
        content: t('settings.credentials.all'),
        selected: kind === 'all',
      },
      ...Object.values(TCCredentialKind).map((value) => ({
        id: value,
        content: t(CREDENTIAL_KIND_LABEL_KEY_BY_KIND[value]),
        selected: kind === value,
      })),
    ],
    [kind, t]
  );

  const selectedKindLabel =
    kindOptions.find((option) => option.selected)?.content ??
    t('settings.credentials.all');

  const hasFilters =
    (provider && provider.trim().length > 0) || (kind && kind !== 'all');

  return (
    <div className="flex flex-col gap-3 p-4 border-b bg-gray-50">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 min-w-[180px] max-w-[240px] flex-1">
          <SearchableSelect
            size="md"
            options={providerOptions}
            value={providerOption}
            onChange={(next) => {
              const nextValue = next ?? '';
              setProviderOption(nextValue);

              if (nextValue === '') {
                onProviderChange('');
                return;
              }

              if (nextValue === OTHER_CREDENTIAL_PROVIDER_OPTION) {
                onProviderChange(OTHER_CREDENTIAL_PROVIDER_OPTION);
                return;
              }

              onProviderChange(nextValue);
            }}
            placeholder={t('settings.credentials.provider.all')}
          />
        </div>

        <div className="min-w-[180px] max-w-[240px] w-full">
          <MenuDropdown
            label={selectedKindLabel}
            items={kindOptions.map((option) => ({
              content: option.content,
              selected: option.selected,
              onClick: () => {
                if (option.id === 'all') {
                  onKindChange('all');
                  return;
                }
                onKindChange(option.id as TCCredentialKind);
              },
            }))}
            appearance="outline"
            variant="secondary"
            size="md"
            className="w-full"
          />
        </div>

        {hasFilters && (
          <Button
            variant="primary"
            appearance="soft"
            size="md"
            onClick={onClear}
            className="flex items-center gap-2"
          >
            <PiFunnelX className="w-4 h-4" />
            <Text size="small">{t('settings.credentials.filters.clear')}</Text>
          </Button>
        )}
      </div>
    </div>
  );
};
